import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  useTheme,
  useMediaQuery,
  Breadcrumbs,
  Link,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Backdrop, Checkbox
} from "@mui/material";
import { ArrowForward, LocalShipping, Close } from "@mui/icons-material";
import { load } from "@cashfreepayments/cashfree-js";
import AddressDialog from "../../Components/AddressDialog/AddressDialog";
import { useLocation, useNavigate } from "react-router-dom";
import { getData, postData } from "../../Webservice/Webservice";
import Swal from "sweetalert2";
import { useCart } from "../../context/CartContext";

export default function Checkout() {
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [shippingAddress, setShippingAddress] = useState(null);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [couponDialogOpen, setCouponDialogOpen] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const cashfreeInstance = useRef(null);
  const { fetchCartCount } = useCart();

  const [walletChecked, setWalletChecked] = useState(false);
  const [pointsChecked, setPointsChecked] = useState(false);
  const [currentCheckoutData, setCurrentCheckoutData] = useState(null);
  const [paymentMethodDialogOpen, setPaymentMethodDialogOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const cashfreeMode = process.env.REACT_APP_CASHFREE_MODE || "sandbox";

  const checkoutData = location.state?.checkoutData?.checkout;
  const walletBalance = location.state?.checkoutData?.walletBalance;
  const availablePoints = location.state?.checkoutData?.availablePoints;

  useEffect(() => {
    if (!checkoutData) {
      navigate('/addtocart');
      return;
    }

    // Initialize currentCheckoutData with the initial checkout data
    setCurrentCheckoutData(location.state?.checkoutData);

    if (checkoutData.addressId) {
      setSelectedAddressId(checkoutData.addressId);
      fetchAddressDetails(checkoutData.addressId);
    }

    fetchCoupons();
  }, [checkoutData, navigate, location.state?.checkoutData]);

  const fetchAddressDetails = async (addressId) => {
    try {
      setLoadingAddress(true);
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      if (!token) return;

      const response = await getData('address/list', {}, token);
      if (response && response.addresses) {
        const address = response.addresses.find(addr => addr._id === addressId);
        if (address) {
          setShippingAddress(address);
        }
      }
    } catch (error) {
      console.error("Error fetching address details:", error);
    } finally {
      setLoadingAddress(false);
    }
  };

  const fetchCoupons = async () => {
    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      if (!token) return;

      const response = await getData('coupon/coupons', {}, token);
      if (response && response.coupons) {
        setCoupons(response.coupons);
      }
    } catch (error) {
      console.error("Error fetching coupons:", error);
    }
  };

  const handleWalletToggle = async (checked) => {
    setWalletChecked(checked);
    await updateCheckoutOptions(checked, pointsChecked);
  };

  const handlePointsToggle = async (checked) => {
    setPointsChecked(checked);
    await updateCheckoutOptions(walletChecked, checked);
  };

  const updateCheckoutOptions = async (useWallet, usePoints, addressId) => {
    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      if (!token) return;

      const payload = {
        addressId: addressId !== undefined ? addressId : (selectedAddressId || ""),
        couponCode: couponCode || "",
        useRewardPoints: usePoints !== undefined ? usePoints : pointsChecked,
        useWalletAmount: useWallet !== undefined ? useWallet : walletChecked,
        sessionId: checkoutData?._id || ""
      };

      const response = await postData('order/checkout/update', payload, token);

      if (response) {
        console.log("Checkout updated successfully!");
        // Update the current checkout data state to trigger re-render
        setCurrentCheckoutData(response);
      } else {
        console.log(response?.message || "Failed to update checkout. Please try again.");
      }
    } catch (error) {
      console.error("Error updating checkout:", error);
      // Revert the checkbox state on error if needed
      if (useWallet !== undefined) setWalletChecked(!useWallet);
      if (usePoints !== undefined) setPointsChecked(!usePoints);
    }
  };

  const handleApplyCoupon = async (code) => {
    if (!code) {
      alert("Please enter a coupon code");
      return;
    }

    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      if (!token) {
        alert("Please sign in to apply coupons.");
        return;
      }

      const payload = {
        addressId: selectedAddressId || "",
        couponCode: code,
        sessionId: checkoutData?._id || ""
      };

      const response = await postData('order/checkout/update', payload, token);

      if (response) {
        console.log("Coupon applied successfully!");
        // Update the current checkout data state to trigger re-render
        setCurrentCheckoutData(response);
      } else {
        console.log(response?.message || "Failed to apply coupon. Please try again.");
      }
    } catch (error) {
      console.error("Error applying coupon:", error);
      alert("An error occurred while applying the coupon.");
    }
  };

  const handleRemoveCoupon = async () => {
    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      if (!token) {
        alert("Please sign in to remove coupon.");
        return;
      }

      const payload = {
        addressId: selectedAddressId || "",
        couponCode: "",
        sessionId: checkoutData?._id || ""
      };

      const response = await postData('order/checkout/update', payload, token);
      if (response) {
        setCouponCode("");
        setCurrentCheckoutData(response);
      } else {
        console.log(response?.message || "Failed to remove coupon. Please try again.");
      }
    } catch (error) {
      console.error("Error removing coupon:", error);
      alert("An error occurred while removing the coupon.");
    }
  };

  const handlePlaceOrder = async () => {
    if (!shippingAddress) {
      alert("Please select a shipping address.");
      return;
    }
    // Open payment method dialog
    setPaymentMethodDialogOpen(true);
  };

  const handlePaymentMethodSelect = async (method) => {
    setPaymentMethodDialogOpen(false);

    if (method === 'COD') {
      await processCODOrder();
    } else if (method === 'ONLINE') {
      await processOnlinePayment();
    }
  };

  const processCODOrder = async () => {
    try {
      setLoadingOrder(true);
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      if (!token) {
        alert("Please sign in to place an order.");
        return;
      }

      const payload = {
        paymentMethod: "COD"
      };

      const response = await postData('order/place-order', payload, token);

      if (response) {
        await Swal.fire({
          icon: "success",
          title: "Order Placed",
          html: `
            <div style="
              font-size: 15px;
              margin-top: 8px;
              color: #e6c10dff;
              font-weight: 600;
            ">
              💐 Order Placed Successfully!
            </div>
          `,
          width: 380,
          background: "#111",
          color: "white",
          confirmButtonText: "OK",
          buttonsStyling: false,
          didRender: () => {
            const btn = document.querySelector('.swal2-confirm');
            if (btn) {
              btn.style.backgroundColor = "#e6c10dff";
              btn.style.color = "#000";
              btn.style.padding = "10px 28px";
              btn.style.borderRadius = "50px";
              btn.style.fontWeight = "bold";
              btn.style.fontSize = "15px";
              btn.style.border = "none";
              btn.style.cursor = "pointer";
            }
          }
        });
        await fetchCartCount();
        navigate("/");
      }
    } catch (error) {
      console.error("Error placing COD order:", error);
      alert("An error occurred while placing the order.");
    } finally {
      setLoadingOrder(false);
    }
  };

  const processOnlinePayment = async () => {
    try {
      setLoadingOrder(true);
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      if (!token) {
        alert("Please sign in to place an order.");
        return;
      }

      const payload = {
        paymentMethod: "PREPAID"
      };

      const response = await postData('order/place-order', payload, token);

      if (!response) {
        alert("Failed to place order. Please try again.");
        return;
      }

      if (response.paymentGateway) {
        const paymentSessionId = response.payment_session_id || response.paymentSessionId;

        if (!paymentSessionId) {
          alert("Payment session missing. Please contact support.");
          return;
        }

        try {
          if (!cashfreeInstance.current) {
            cashfreeInstance.current = await load({ mode: cashfreeMode });
          }

          await cashfreeInstance.current.checkout({
            paymentSessionId,
            redirectTarget: "_modal"
          });

          const verifyPayload = {
            order_id: response.order_id || response.orderId
          };

          setVerifyingPayment(true);
          let verifyResponse;
          try {
            verifyResponse = await postData('order/payment-verify', verifyPayload, token);
          } finally {
            setVerifyingPayment(false);
          }

          if (verifyResponse) {
            await Swal.fire({
              icon: "success",
              title: "Payment Verified",
              html: `
                <div style="
                  font-size: 15px;
                  margin-top: 8px;
                  color: #e6c10dff;
                  font-weight: 600;
                ">
                  💐 Payment Verified Successfully!
                </div>
              `,
              width: 380,
              background: "#111",
              color: "white",
              confirmButtonText: "OK",
              buttonsStyling: false,
              customClass: {
                popup: "",
                confirmButton: ""
              },
              didRender: () => {
                const btn = document.querySelector('.swal2-confirm');
                if (btn) {
                  btn.style.backgroundColor = "#e6c10dff";
                  btn.style.color = "#000";
                  btn.style.padding = "10px 28px";
                  btn.style.borderRadius = "50px";
                  btn.style.fontWeight = "bold";
                  btn.style.fontSize = "15px";
                  btn.style.border = "none";
                  btn.style.cursor = "pointer";
                }

                const popup = document.querySelector('.swal2-popup');
                if (popup) {
                  popup.style.borderRadius = "18px";
                  popup.style.border = "1px solid #333";
                  popup.style.boxShadow = "0 8px 25px rgba(0,0,0,0.6)";
                }
              }
            });
            await fetchCartCount();
            navigate("/");
          } else {
            await Swal.fire({
              icon: "error",
              title: "Verification Failed",
              text: "Payment Verification Failed",
              confirmButtonText: "OK",
              buttonsStyling: false,
              didRender: () => {
                const btn = document.querySelector('.swal2-confirm');
                if (btn) {
                  btn.style.backgroundColor = "#ff3b3b";
                  btn.style.color = "#fff";
                  btn.style.padding = "10px 28px";
                  btn.style.borderRadius = "50px";
                  btn.style.fontWeight = "bold";
                  btn.style.border = "none";
                  btn.style.cursor = "pointer";
                }
              }
            });
          }
        } catch (paymentError) {
          console.error("Cashfree payment failed:", paymentError);
          alert("Unable to initiate payment. Please try again.");
        }
        return;
      }

      alert("Order placed successfully!");
    } catch (error) {
      console.error("Error placing order:", error);
      alert("An error occurred while placing the order.");
    } finally {
      setLoadingOrder(false);
    }
  };

  const handleAddressChange = (event) => {
    const newAddressId = event.target.value;
    setSelectedAddressId(newAddressId);
    fetchAddressDetails(newAddressId);
    updateCheckoutOptions(walletChecked, pointsChecked, newAddressId);
  };

  const handleSaveAddress = (selected) => {
    if (selected) {
      setShippingAddress(selected);
      setSelectedAddressId(selected._id);
      updateCheckoutOptions(walletChecked, pointsChecked, selected._id);
    }
  };

  const handleAddNewAddress = () => {
    // AddressDialog handles refresh
  };

  if (!checkoutData) {
    return null;
  }

  // Use currentCheckoutData if available, otherwise fall back to original location state
  const activeCheckoutData = currentCheckoutData?.checkout || checkoutData;
  const activeWalletBalance = currentCheckoutData?.walletBalance || walletBalance;
  const activeAvailablePoints = currentCheckoutData?.availablePoints || availablePoints;
  const isCodAvailable = currentCheckoutData?.isCod ?? location.state?.checkoutData?.isCod ?? false;
  const isOnlinePaymentAvailable = currentCheckoutData?.isOnlinepayment ?? location.state?.checkoutData?.isOnlinepayment ?? true;

  const cartItems = activeCheckoutData.cart.items || [];
  const { subtotal, gsttotal, discountTotal, deliveryCharges, totalAmount } = activeCheckoutData.cart;
  const finalTotal = activeCheckoutData.finalAmount;
  const deliveryFee = activeCheckoutData.deliveryCharges;
  const walletUsedAmount = activeCheckoutData.walletUsedAmount || 0;
  const redeemPointsAmount = activeCheckoutData.redeemPointsAmount || 0;

  return (
    <Box sx={{
      maxWidth: 1200,
      mx: "auto",
      p: { xs: 2, sm: 3 },
      backgroundColor: ' #F6F4EF',
      minHeight: '100vh'
    }}>

      {/* Header */}
      < Box sx={{ textAlign: "center", mb: 6 }}>
        <Typography
          variant="h4"
          align="center"
          sx={{
            fontSize: { xs: "32px", sm: "48px", md: "56px" },
            color: "#222",
            fontWeight: 900,
            lineHeight: 1.1,
            letterSpacing: "3.5px",
            textTransform: "uppercase",
            mt: 2
          }}
        >
          CHECKOUT
        </Typography>
      </Box >

      <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, gap: 4 }}>
        {/* Left Column - Shipping Address & Product Details */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Shipping Address Card */}
          <Card sx={{
            mb: 3, boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
            borderRadius: "20px"
          }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <LocalShipping sx={{ mr: 1, color: 'black' }} />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary'
                  }}
                >
                  Shipping Address
                </Typography>
              </Box>

              {loadingAddress ? (
                <CircularProgress size={24} />
              ) : shippingAddress ? (
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>
                        {shippingAddress.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" >
                        {shippingAddress.mobile}
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setAddressDialogOpen(true)}
                      sx={{ textTransform: 'none', color: 'black', border: '1px solid black' }}
                    >
                      Change
                    </Button>
                  </Box>

                  <Typography variant="body1" >
                    {shippingAddress.addressLine1}
                    {shippingAddress.addressLine2 ? `, ${shippingAddress.addressLine2}` : ''}
                    {shippingAddress.landmark ? `, ${shippingAddress.landmark}` : ''}
                  </Typography>

                  <Typography variant="body1" >
                    {shippingAddress.city}, {shippingAddress.state} {shippingAddress.pincode}
                  </Typography>

                  <Typography variant="body1" >
                    {shippingAddress.country}
                  </Typography>
                </Stack>
              ) : (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    No shipping address selected.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => setAddressDialogOpen(true)}
                    sx={{ bgcolor: 'black', '&:hover': { bgcolor: '#333' } }}
                  >
                    Select Address
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Product Details Card */}
          <Card sx={{
            boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
            borderRadius: '20px'
          }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  color: 'text.primary'
                }}
              >
                Order Items ({cartItems.length})
              </Typography>

              <Stack spacing={3}>
                {cartItems.map((item) => (
                  <Box key={item.product_id} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        backgroundColor: 'grey.100',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        overflow: 'hidden'
                      }}
                    >
                      <img
                        src={Array.isArray(item.image) ? item.image[0] : item.image}
                        alt={item.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover"
                        }}
                      />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        sx={{ mb: 0.5 }}
                        noWrap
                      >
                        {item.name}
                      </Typography>

                      <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary" >
                          Brand: {item.brand}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" >
                          Unit: {item.unitValue} {item.unitName}
                        </Typography>
                      </Stack>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" >
                          Qty: {item.quantity}
                        </Typography>
                        <Typography variant="body1" fontWeight={600} >
                          ₹{item.finalPrice ? item.finalPrice.toFixed(2) : item.price}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Right Column - Order Summary & Coupon */}
        <Box sx={{
          width: { xs: "100%", lg: 400 },
          position: { xs: "static", lg: "sticky" },
          top: 20
        }}>
          <Card sx={{
            boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
            borderRadius: "20px"
          }}>
            <CardContent sx={{ p: 3.5 }}>
              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  letterSpacing: "3px",
                  color: "#6b6969ff",
                  mb: 3,
                  fontWeight: 700,
                }}
              >
                Order Summary
              </Typography>

              {/* Coupons Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                  Coupon Code
                </Typography>

                {activeCheckoutData?.couponCode ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '2px' }}>
                      {activeCheckoutData.couponCode}
                    </Typography>
                    <IconButton onClick={handleRemoveCoupon} sx={{ ml: 'auto' }} aria-label="remove-coupon">
                      <Close />
                    </IconButton>
                  </Box>
                ) : (
                  <>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter Coupon Code"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                        sx={{ bgcolor: 'white' }}
                      />
                      <Button
                        variant="contained"
                        sx={{ bgcolor: 'black', color: 'white', '&:hover': { bgcolor: '#333' } }}
                        onClick={() => handleApplyCoupon(couponCode)}
                      >
                        APPLY
                      </Button>
                    </Stack>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setCouponDialogOpen(true)}
                      sx={{ mt: 1, textTransform: 'none', color: 'primary.main', fontWeight: 600 }}
                    >
                      View Available Coupons
                    </Button>
                  </>
                )}

              </Box>

              {/* Wallet & Points Section */}
              <Box sx={{
                mb: 3,
                p: 2,
                bgcolor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e0e0e0'
              }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  Available Balance & Points
                </Typography>

                <Stack spacing={1.5}>
                  {/* Wallet Balance Row */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Checkbox
                        size="small"
                        checked={walletChecked}
                        onChange={e => handleWalletToggle(e.target.checked)}
                        color="success"
                        sx={{
                          transform: "scale(1.4)",   // Increase size (1.0 = normal)
                          "& .MuiSvgIcon-root": {
                            fontSize: 18,            // Increase icon size
                          }
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Wallet Balance
                      </Typography>
                    </Box>

                    <Typography variant="body1" fontWeight={600} color="success.main">
                      ₹{activeWalletBalance?.toFixed(2) || '0.00'}
                    </Typography>
                  </Box>

                  {/* Available Points Row */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Checkbox
                        size="small"
                        checked={pointsChecked}
                        onChange={e => handlePointsToggle(e.target.checked)}
                        color="success"
                        sx={{
                          transform: "scale(1.4)",   // Increase size (1.0 = normal)
                          "& .MuiSvgIcon-root": {
                            fontSize: 18,            // Increase icon size
                          }
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Available Points
                      </Typography>
                    </Box>

                    <Typography variant="body1" fontWeight={600} color="primary.main">
                      {activeAvailablePoints || 0} pts
                    </Typography>
                  </Box>
                </Stack>
              </Box>


              {/* Pricing Breakdown */}
              <Stack spacing={2.5} sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center' }}>
                  <Typography variant="body1" color="text.secondary" fontWeight={500} >
                    Subtotal ({cartItems.length} items)
                  </Typography>
                  <Typography variant="body1" fontWeight={600} color="text.primary" >
                    ₹{subtotal?.toFixed(2)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center' }}>
                  <Typography variant="body1" color="text.secondary" fontWeight={500} >
                    Discount
                  </Typography>
                  <Typography variant="body1" fontWeight={600} color="text.primary" >
                    -₹{discountTotal?.toFixed(2)}
                  </Typography>
                </Box>

                {/* <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center' }}>
                  <Typography variant="body1" color="text.secondary" fontWeight={500} >
                    GST
                  </Typography>
                  <Typography variant="body1" fontWeight={600} color="text.primary" >
                    ₹{gsttotal?.toFixed(2)}
                  </Typography>
                </Box> */}

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center' }}>
                  <Typography variant="body1" color="text.secondary" fontWeight={500} >
                    Delivery Fee
                  </Typography>
                  <Typography variant="body1" fontWeight={600} color="text.primary" >
                    ₹{deliveryFee?.toFixed(2)}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1, borderColor: 'divider' }} />

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center' }}>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    color="text.primary"
                    sx={{
                      fontSize: { xs: "1.25rem", sm: "1.5rem" },
                      letterSpacing: "3px",
                      color: "#3e3c3cff",
                      fontWeight: 700,
                    }}
                  >
                    Total
                  </Typography>
                  <Typography variant="h6" fontWeight={800} color="text.primary" >
                    ₹{finalTotal?.toFixed(2)}
                  </Typography>
                </Box>
              </Stack>

              <Button
                variant="contained"
                onClick={handlePlaceOrder}
                disabled={loadingOrder}
                endIcon={loadingOrder ? <CircularProgress size={20} color="inherit" /> : <ArrowForward />}
                fullWidth
                sx={{
                  py: 1.5,
                  bgcolor: "white",
                  color: "black",
                  fontSize: "14px",
                  fontWeight: 600,
                  letterSpacing: "2px",
                  border: "1px solid black",
                  "&:hover": {
                    color: "white",
                    bgcolor: "black",
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                {loadingOrder ? "Processing..." : "Continue to Payment"}
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Address Dialog */}
      <AddressDialog
        open={addressDialogOpen}
        onClose={() => setAddressDialogOpen(false)}
        selectedAddress={selectedAddressId}
        onAddressChange={handleAddressChange}
        onSave={handleSaveAddress}
        onAddNewSave={handleAddNewAddress}
        isMobile={isMobile}
      />

      {/* Coupon Dialog */}
      <Dialog
        open={couponDialogOpen}
        onClose={() => setCouponDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Available Coupons
          <IconButton onClick={() => setCouponDialogOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent >
          {coupons.length > 0 ? (
            <Stack spacing={2}>
              {coupons.map((coupon) => (
                <Card
                  key={coupon.code}
                  variant="outlined"
                  sx={{
                    borderStyle: 'dashed',
                    borderColor: coupon.isEligible === false ? '#d32f2f' : 'black',
                    backgroundColor: coupon.isEligible === false ? '#fff5f5' : "#eeeeee7c",
                    opacity: coupon.isEligible === false ? 0.9 : 1
                  }}
                >
                  <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ flex: 1, mr: 2 }}>
                      <Typography variant="subtitle1" fontWeight={700} color={coupon.isEligible === false ? '#d32f2f' : "black"}>
                        {coupon.code}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {coupon.description || (coupon.type === 'percentage' ? `${coupon.discount}% Off` : `₹${coupon.discount} Off`)}
                      </Typography>

                      {coupon.isEligible === false && (
                        <Box sx={{ mt: 1, p: 1, bgcolor: '#fee2e2', borderRadius: 1, border: '1px solid #fecaca' }}>
                          <Typography variant="caption" display="block" sx={{ color: '#991b1b', fontWeight: 600 }}>
                            Not Eligible: {coupon.inactiveReason}
                          </Typography>
                        </Box>
                      )}

                      {coupon.minOrderAmount > 0 && coupon.isEligible !== false && (
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                          Min Order: ₹{coupon.minOrderAmount}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={coupon.isEligible === false}
                      onClick={() => {
                        setCouponCode(coupon.code);
                        setCouponDialogOpen(false);
                      }}
                      sx={{
                        color: coupon.isEligible === false ? "#999" : "black",
                        borderColor: coupon.isEligible === false ? "#ccc" : "black",
                        "&.Mui-disabled": {
                          borderColor: "#eee",
                          color: "#999"
                        }
                      }}
                    >
                      {coupon.isEligible === false ? "Locked" : "Select"}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No coupons available.</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={paymentMethodDialogOpen}
        onClose={() => setPaymentMethodDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1.5,
          }
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.35rem",
            pb: 1,
          }}
        >
          Select Payment Method
        </DialogTitle>

        <DialogContent sx={{ pt: 1, pb: 3 }}>
          <Stack spacing={2}>

            {/* Cash on Delivery */}
            {isCodAvailable ? (
              <Button
                fullWidth
                variant="outlined"
                onClick={() => handlePaymentMethodSelect("COD")}
                startIcon={<span style={{ fontSize: "1.4rem" }}>💵</span>}
                sx={{
                  py: 1.8,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: "1.05rem",
                  borderWidth: 2,
                  textTransform: "none",
                  backgroundColor: "#f5f5f5",
                  borderColor: "black",
                  color: "black",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                    borderColor: "black",
                  }
                }}
              >
                Cash on Delivery
              </Button>
            ) : (
              <Box
                sx={{
                  py: 2,
                  px: 2,
                  borderRadius: 2,
                  backgroundColor: "#fff3cd",
                  border: "1px solid #ffc107",
                  textAlign: "center"
                }}
              >
                <Typography variant="body1" sx={{ color: "#856404", fontWeight: 600 }}>
                  ⚠️ Cash on Delivery is not available
                </Typography>
                <Typography variant="body2" sx={{ color: "#856404", mt: 0.5 }}>
                  Please select Pay Online option
                </Typography>
              </Box>
            )}

            {/* Online Payment */}
            <Button
              fullWidth
              variant="contained"
              onClick={() => handlePaymentMethodSelect("ONLINE")}
              startIcon={<span style={{ fontSize: "1.4rem" }}>💳</span>}
              sx={{
                py: 1.8,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: "1.05rem",
                backgroundColor: "black",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#333",
                }
              }}
            >
              Pay Online (UPI / Card)
            </Button>

          </Stack>
        </DialogContent>
      </Dialog>


      {/* Payment Verification Loader */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={verifyingPayment}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box >
  );
}